import Button from 'atoms/Button';
import cn from 'classnames';
import NotificationAlert from 'molecules/NotificationAlert';
import React, { useCallback, useState } from 'react';
import { OfferingType } from 'stores/networkInterfaces';
import { navigateToAuthUrl } from 'utils/authUtils';
import { useOrderActions } from 'utils/hooks/useOrderActions';
import { navigateToUrl } from 'utils/navigateUtils';
import { safeRunRequest } from 'utils/networkRequests';

import s from './InvestmentActions.module.scss';

interface IInvestmentActions {
  className?: string;
  enableActions?: boolean;
  isAuthenticated?: boolean;
  isApproved?: boolean;
  isManagement?: boolean;
  isLimitedOffering?: boolean;
}

const InvestmentActions = ({
  className,
  enableActions,
  isAuthenticated,
  isApproved,
  isManagement,
  isLimitedOffering
}: IInvestmentActions) => {
  const {
    buyActionLabel,
    sellActionLabel,
    buyAction,
    sellAction,
    buyEnabled,
    sellEnabled,
    buyPending,
    offeringId,
    offeringType
  } = useOrderActions();

  const [notification, setNotification] = useState('');

  const onSignIn = () => {
    return navigateToAuthUrl({ isAuthenticated });
  };

  const onClose = useCallback(() => setNotification(''), []);

  const onExpressInterest = async () => {
    const body = { offering_id: offeringId };

    await safeRunRequest('expressOfferingInterest', body);

    setNotification(
      'Thanks for your interest. A member of the USREM team will reach out to you shortly.'
    );
  };

  const onNavigate = (url: string) => {
    if (offeringId) {
      return navigateToUrl(url, { query: { id: offeringId } });
    }
  };

  const enableBuyAction =
    (!isApproved || !isManagement) &&
    offeringId &&
    enableActions &&
    !isLimitedOffering &&
    (buyEnabled || buyPending);
  const enableSellAction =
    (!isApproved || !isManagement) &&
    offeringId &&
    enableActions &&
    !isLimitedOffering &&
    sellEnabled &&
    offeringType === OfferingType.secondary;
  const enableContactAction =
    (!isApproved || !isManagement) &&
    offeringId &&
    enableActions &&
    (isLimitedOffering || offeringType != OfferingType.secondary);

  return (
    <div className={cn(s.InvestmentActions, className)}>
      {isApproved && isManagement && (
        <Button size="large" onClick={() => onNavigate('/admin/orders')}>
          {'Orders'}
        </Button>
      )}
      {enableBuyAction && (
        <Button
          aria-label="Buy"
          className={s.InvestmentActions__button}
          size="large"
          color="primary"
          onClick={() => {
            if (isApproved) {
              buyAction();
            } else {
              onSignIn();
            }
          }}
        >
          {buyActionLabel}
        </Button>
      )}
      {enableSellAction && (
        <Button
          className={s.InvestmentActions__button}
          color="secondary"
          size="large"
          onClick={() => {
            if (isApproved) {
              sellAction();
            } else {
              onSignIn();
            }
          }}
        >
          {sellActionLabel}
        </Button>
      )}
      {enableContactAction && (
        <Button
          className={s.InvestmentActions__button}
          color="secondary"
          size="large"
          onClick={() => {
            onExpressInterest();
          }}
        >
          {'Express Interest'}
        </Button>
      )}

      <NotificationAlert
        open={!!notification}
        severity="success"
        onClose={onClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {notification}
      </NotificationAlert>
    </div>
  );
};

export default InvestmentActions;
